export { ICONS, type IconName } from './icons';

/** Default locale for platform-admin UI labels and `X-Locale` API hints. */
export const DEFAULT_PLATFORM_ADMIN_LOCALE = 'en-US' as const;

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? '/api/admin';

export const DEFAULT_PAGE_SIZE = 10;

export const ROUTES = {
  AUTH: {
    LOGIN: '/login',
  },
  DASHBOARD: {
    OVERVIEW: '/overview',
    USERS: '/users',
    PERMISSIONS: '/permissions',
    /** EPIC-31 — Platform Admin System-role administration. */
    ROLES: '/roles',
    I18N: {
      ROOT: '/i18n',
      TRANSLATIONS: '/i18n/translations',
      WORKFLOW: {
        PRE_REVIEW: '/i18n/workflow/pre-review',
        REVIEW: '/i18n/workflow/review',
        CORRECTIONS: '/i18n/workflow/corrections',
        CHANGES: '/i18n/workflow/changes',
      },
      TOOLS: {
        DUPLICATES: '/i18n/tools/duplicates',
        REPLICATION: '/i18n/tools/replication',
      },
    },
    TENANTS: {
      ROOT: '/tenants',
      SYNC_CONFIG: '/tenants/:uuid/sync-config',
    },
    ANALYTICS: '/analytics',
    FIGMA_LENS: '/figma/snapshots',
    BUGS: {
      ROOT: '/bugs',
      DETAIL: '/bugs',
      DASHBOARD: '/bugs/dashboard',
    },
    ENDPOINTS: {
      ROOT: '/endpoints',
      DETAIL: '/endpoints',
    },
    ERROR_CATALOG: '/errors',
    MCP_CONNECTIONS: '/mcp-connections',
    NOTIFICATION_WORKFLOWS: {
      ROOT: '/notification-workflows',
      DETAIL: '/notification-workflows',
    },
    MY_TICKETS: {
      ROOT: '/my-tickets',
      DETAIL: '/my-tickets',
    },
    SETTINGS: {
      I18N: '/settings/i18n',
      /**
       * EPIC-31 Feature 31.7 — environment replication for translations,
       * permissions and System roles. Moved out of the Translations sub-menu:
       * the mechanism was never i18n-specific.
       */
      REPLICATION: '/settings/replication',
      PROFILE: {
        ROOT: '/settings/profile',
        API_KEYS: '/settings/profile/api-keys',
      },
    },
  },
} as const;

export const SESSION_COOKIE_NAME = 'platform_admin_session_id';

/**
 * Stable, long-lived per-browser device identifier. Generated once by the
 * middleware (never rotated on login/logout) and forwarded to the backend as
 * `fingerprintId` so it can reuse the existing session slot for this browser.
 */
export const DEVICE_ID_COOKIE_NAME = 'pa_device_id';

/** 2 years, in seconds — maxAge for the device id cookie. */
export const DEVICE_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 2;

export const QUERY_KEYS = {
  AUTH_SESSION: 'auth-session',
  USER: 'user',
  PERMISSIONS: 'permissions',
  TRANSLATIONS: 'translations',
  ANALYTICS: 'analytics',
  I18N_SETTINGS: 'i18n-settings',
  API_KEYS: 'api-keys',
  MCP_CONNECTIONS: 'mcp-connections',
  BUGS: 'bugs',
  BUG_DETAIL: 'bug-detail',
  BUG_COMMENTS: 'bug-comments',
  BUG_HISTORY: 'bug-history',
  MY_TICKETS: 'my-tickets',
  BUG_TEAM_MEMBERS: 'bug-team-members',
  BUG_PARENT_STORIES: 'bug-parent-stories',
  BUG_CUSTOMER_CONVERSATION: 'bug-customer-conversation',
  BUG_CUSTOMER_TICKETS: 'bug-customer-tickets',
  ERROR_CATALOG: 'error-catalog',
  ENDPOINT_AUDIT_RUNS: 'endpoint-audit-runs',
  ENDPOINT_AUDIT_REPORT: 'endpoint-audit-report',
  AZURE_DEVOPS_AREAS: 'azure-devops-areas',
  AZURE_DEVOPS_ITERATIONS: 'azure-devops-iterations',
  TENANTS: 'tenants',
  /** Epic 13 — platform tenant user directory (BFF `/api/users`). */
  PLATFORM_USERS: 'platform-users',
  PLATFORM_USER_DETAIL: 'platform-user-detail',
  PLATFORM_USER_LOGS: 'platform-user-logs',
} as const;
